// VulnerabilityModal.jsx
import React, { useState, useEffect } from 'react';
import './VulnerabilityModal.css';
import config from '../config';

function VulnerabilityModal({ isOpen, onClose, severity, severityLabel, customerId, assessmentId }) {
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && severity) {
      fetchVulnerabilities();
    }
  }, [isOpen, severity, customerId, assessmentId]);

  const fetchVulnerabilities = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.apiUrl}/api/vulnerabilities/list?customer_id=${customerId}&severity=${severity}&assessment_id=${assessmentId}&limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('cognitoIdToken')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setVulnerabilities(data.vulnerabilities);
      }
    } catch (error) {
      console.error('Error fetching vulnerabilities:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getSeverityColor = (sev) => {
    const colors = {
      5: '#dc2626',
      4: '#ea580c',
      3: '#f59e0b',
      2: '#84cc16'
    };
    return colors[sev] || '#6b7280';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span 
              className="severity-badge-large" 
              style={{ backgroundColor: getSeverityColor(severity) }}
            >
              {severityLabel.toUpperCase()}
            </span>
            {' '}Vulnerabilities ({vulnerabilities.length})
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">Loading vulnerabilities...</div>
          ) : (
            <div className="vulnerability-list">
              {vulnerabilities.map((vuln) => (
                <div key={vuln.vuln_id} className="vulnerability-item">
                  <div className="vuln-header">
                    <div className="vuln-title-row">
                      <h3>{vuln.title}</h3>
                      {vuln.exploitability === 'Exploitable' && (
                        <span className="exploitable-badge">⚠️ Exploitable</span>
                      )}
                    </div>
                    <div className="vuln-meta">
                      <span className="vuln-host">{vuln.hostname || vuln.ip_address}</span>
                      {vuln.port > 0 && (
                        <span className="vuln-port">Port {vuln.port}/{vuln.protocol}</span>
                      )}
                      {vuln.cve_id && (
                        <span className="vuln-cve">{vuln.cve_id}</span>
                      )}
                    </div>
                  </div>

                  <div className="vuln-details">
                    {vuln.threat && (
                      <div className="vuln-section">
                        <strong>Threat:</strong>
                        <p>{vuln.threat}</p>
                      </div>
                    )}

                    {vuln.impact && (
                      <div className="vuln-section">
                        <strong>Impact:</strong>
                        <p>{vuln.impact}</p>
                      </div>
                    )}

                    {vuln.solution && (
                      <div className="vuln-section vuln-solution">
                        <strong>Solution:</strong>
                        <p>{vuln.solution}</p>
                      </div>
                    )}

                    {vuln.results && (
                      <div className="vuln-section vuln-results">
                        <strong>Detection:</strong>
                        <code>{vuln.results}</code>
                      </div>
                    )}

                    {vuln.associated_malware && (
                      <div className="vuln-malware">
                        <span className="malware-badge">🦠 {vuln.associated_malware}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default VulnerabilityModal;
